import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, Calendar, FileText, BarChart3, Shield, Zap, Check } from "lucide-react";
import { PageHeader } from "@/components/layout/Navigation";

const iconMap = { Users, Calendar, FileText, BarChart3, Shield, Zap };

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
};

const solutions = [
  {
    slug: "client-management",
    icon: "Users",
    title: "Client Management",
    tagline: "Centralized Patient Data",
    description: "Keep all your patient information organized and accessible. Our client management system provides a 360-degree view of every patient, from contact details to visit history.",
    benefits: ["Unified patient profiles", "Contact & communication history", "Custom fields & tags", "Secure data storage", "Quick search & filters"],
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800"
  },
  {
    slug: "scheduling",
    icon: "Calendar",
    title: "Smart Scheduling",
    tagline: "Automated Appointments",
    description: "Reduce no-shows and administrative burden with intelligent scheduling. Online booking, automated reminders, and calendar sync keep your practice running smoothly.",
    benefits: ["Online self-booking", "Automated reminders", "Calendar sync (Google, Outlook)", "Waitlist management", "Recurring appointments"],
    image: "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=800"
  },
  {
    slug: "documentation",
    icon: "FileText",
    title: "Documentation",
    tagline: "Go Paperless",
    description: "Streamline your documentation workflow with digital forms, templates, and secure storage. Reduce errors and save time with smart form automation.",
    benefits: ["Custom form builder", "E-signatures", "Template library", "Automatic filing", "Version history"],
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800"
  },
  {
    slug: "analytics",
    icon: "BarChart3",
    title: "Analytics & Reports",
    tagline: "Data-Driven Insights",
    description: "Make informed decisions with real-time analytics. Track key metrics, identify trends, and generate reports to optimize your practice performance.",
    benefits: ["Revenue tracking", "Appointment analytics", "Patient retention metrics", "Custom dashboards", "Automated reports"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800"
  },
  {
    slug: "integrations",
    icon: "Zap",
    title: "Integrations",
    tagline: "Connected Ecosystem",
    description: "Connect NeoBody with your existing tools. Our platform integrates with leading EHRs, billing systems, labs, and telehealth providers.",
    benefits: ["EHR integration", "Billing & payments", "Lab connections", "Telehealth platforms", "API access"],
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800"
  },
  {
    slug: "security",
    icon: "Shield",
    title: "Security & Compliance",
    tagline: "HIPAA Certified",
    description: "Your patients' data security is our top priority. NeoBody is fully HIPAA compliant with enterprise-grade encryption and security protocols.",
    benefits: ["HIPAA compliant", "256-bit encryption", "Role-based access", "Audit logging", "Regular security audits"],
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800"
  }
];

const ServicesPage = () => {
  return (
    <div data-testid="services-page">
      <PageHeader 
        title="Our Solutions" 
        subtitle="A complete suite of tools to streamline your healthcare operations."
        breadcrumbs={[{ name: "Solutions" }]}
      />

      <section className="py-20 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {solutions.map((solution, index) => {
              const IconComponent = iconMap[solution.icon] || Users;
              return (
                <motion.div key={solution.slug} variants={fadeUp}>
                  <Link 
                    to={`/services/${solution.slug}`} 
                    className="service-card group block h-full"
                    data-testid={`solution-card-${index}`}
                  >
                    <div className="service-card-image">
                      <img src={solution.image} alt={solution.title} />
                    </div>
                    <div className="service-card-content">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full bg-[#1B4D3E]/5 flex items-center justify-center group-hover:bg-[#1B4D3E] transition-colors">
                          <IconComponent className="w-6 h-6 text-[#1B4D3E] group-hover:text-white transition-colors" strokeWidth={1.5} />
                        </div>
                        <span className="text-xs tracking-[0.15em] uppercase text-[#D4AF37] font-medium">
                          {solution.tagline}
                        </span>
                      </div>
                      <h3 className="font-display text-2xl text-[#1A1A1A] mb-3">{solution.title}</h3>
                      <p className="text-[#4A4A4A] font-light leading-relaxed mb-4">
                        {solution.description.substring(0, 120)}...
                      </p>
                      <ul className="space-y-2 mb-6">
                        {solution.benefits.slice(0, 3).map((benefit, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                            <Check className="w-4 h-4 text-[#1B4D3E]" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                      <div className="flex items-center gap-2 text-[#1B4D3E] font-medium group-hover:gap-3 transition-all">
                        Learn More <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] mb-6">
            See It In Action
          </h2>
          <p className="text-lg text-[#4A4A4A] font-light max-w-2xl mx-auto mb-10">
            Schedule a personalized demo to see how NeoBody can transform your healthcare practice.
          </p>
          <Link to="/book" className="btn-gold">
            Request Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
